import { useState } from "react";
import { toast } from "sonner";
import { Gauge, AlertTriangle, Clock, CheckCircle2, TrendingUp, Info } from "lucide-react";
import { api, formatApiError } from "../api";
import { fmtTry, fmtKm } from "../lib/labels";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";

const FUELS = ["Benzin", "Dizel", "LPG", "Hibrit", "Elektrik"];
const TRANSMISSIONS = ["Manuel", "Otomatik", "Yarı Otomatik"];
const BODY_TYPES = ["Sedan", "Hatchback", "SUV", "Station Wagon", "Coupe", "Cabrio", "MPV", "Pickup", "Panelvan", "Minibüs"];

const EMPTY = { brand: "", model: "", year: "", trim: "", engine: "", fuel: "", transmission: "", km: "", body_type: "", damage_info: "", paint_info: "", city: "" };

const VALUATION_NOTE = "Gösterilecek değer tahmini piyasa değeridir; gerçek satış fiyatı aracın durumuna, ekspertiz sonucuna ve pazarlığa göre değişebilir.";

export default function Degerleme() {
  const [form, setForm] = useState({ ...EMPTY });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/degerleme", {
        ...form,
        year: form.year === "" ? null : Number(form.year),
        km: form.km === "" ? null : Number(form.km),
      });
      setResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  return (
    <div data-testid="degerleme-page" className="min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-6">
        <div className="flex items-center gap-3">
          <Gauge className="w-6 h-6 text-cyan-300" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Araç Değerleme</h1>
        </div>
        <p className="text-sm text-slate-400">Aracınızın güncel piyasa ortalama fiyatını öğrenin. Bilgileri eksiksiz girin; değerleme aynı marka/model/yıl/donanıma en yakın araçlar üzerinden hesaplanır.</p>

        <form onSubmit={submit} data-testid="degerleme-form"
          className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Marka *</label>
              <input data-testid="val-brand" required value={form.brand} onChange={(e) => set("brand", e.target.value)} className={inputCls} placeholder="Ford" /></div>
            <div><label className={labelCls}>Model *</label>
              <input data-testid="val-model" required value={form.model} onChange={(e) => set("model", e.target.value)} className={inputCls} placeholder="Focus" /></div>
            <div><label className={labelCls}>Model Yılı *</label>
              <input data-testid="val-year" required type="number" min="1950" max="2030" value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} placeholder="2020" /></div>
            <div><label className={labelCls}>Paket / Donanım</label>
              <input data-testid="val-trim" value={form.trim} onChange={(e) => set("trim", e.target.value)} className={inputCls} placeholder="Titanium" /></div>
            <div><label className={labelCls}>Motor</label>
              <input data-testid="val-engine" value={form.engine} onChange={(e) => set("engine", e.target.value)} className={inputCls} placeholder="1.5 TDCi" /></div>
            <div><label className={labelCls}>Yakıt</label>
              <select data-testid="val-fuel" value={form.fuel} onChange={(e) => set("fuel", e.target.value)} className={inputCls}>
                <option value="">Seçin</option>
                {FUELS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select></div>
            <div><label className={labelCls}>Vites</label>
              <select data-testid="val-transmission" value={form.transmission} onChange={(e) => set("transmission", e.target.value)} className={inputCls}>
                <option value="">Seçin</option>
                {TRANSMISSIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Kilometre *</label>
              <input data-testid="val-km" required type="number" min="0" value={form.km} onChange={(e) => set("km", e.target.value)} className={inputCls} placeholder="85000" /></div>
            <div><label className={labelCls}>Kasa Tipi</label>
              <select data-testid="val-body" value={form.body_type} onChange={(e) => set("body_type", e.target.value)} className={inputCls}>
                <option value="">Seçin</option>
                {BODY_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
              </select></div>
            <div><label className={labelCls}>Bölge / İl</label>
              <input data-testid="val-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} placeholder="İstanbul" /></div>
            <div><label className={labelCls}>Hasar / Tramer</label>
              <input data-testid="val-damage" value={form.damage_info} onChange={(e) => set("damage_info", e.target.value)} className={inputCls} placeholder="Tramersiz / 15.000 ₺ tramer" /></div>
            <div><label className={labelCls}>Boya / Değişen</label>
              <input data-testid="val-paint" value={form.paint_info} onChange={(e) => set("paint_info", e.target.value)} className={inputCls} placeholder="Boyalı yok / 2 parça boyalı" /></div>
          </div>
          <button data-testid="val-submit" type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 transition-all disabled:opacity-50">
            <Gauge className="w-4 h-4" /> {loading ? "Hesaplanıyor..." : "Piyasa Değerini Hesapla"}
          </button>
        </form>

        {result && result.status === "ok" && (
          <div data-testid="val-result" className="rounded-2xl border border-emerald-500/30 bg-slate-900/60 backdrop-blur-xl p-6 sm:p-8 animate-fade-up space-y-5">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-bold text-white">Değerleme Sonucu</h2>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tahmini Ortalama Piyasa Değeri</p>
              <p data-testid="val-avg" className="text-4xl font-bold text-emerald-300 mt-1">{fmtTry(result.avg_price)}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-white/10 bg-slate-950/60 p-4">
                <p className="text-xs text-slate-500">Piyasa Aralığı</p>
                <p data-testid="val-range" className="text-sm font-bold text-slate-100 mt-1">{fmtTry(result.min_price)} – {fmtTry(result.max_price)}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-slate-950/60 p-4">
                <p className="text-xs text-slate-500">Benzer Araç / İlan</p>
                <p data-testid="val-count" className="text-sm font-bold text-slate-100 mt-1">{result.sample_size ?? "—"}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-slate-950/60 p-4">
                <p className="text-xs text-slate-500">Son Güncelleme</p>
                <p data-testid="val-updated" className="text-sm font-bold text-slate-100 mt-1">{result.updated_at || "—"}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Hesaplama Kriterleri</p>
              <div className="flex flex-wrap gap-2">
                {(result.criteria || []).map((c) => (
                  <span key={c} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-300">{c}</span>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-500 flex items-start gap-1.5 border-t border-white/5 pt-4">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-300" /> {result.disclaimer}
            </p>
          </div>
        )}

        {result && result.status === "veri_bekleniyor" && (
          <div data-testid="val-result" className="rounded-2xl border border-amber-500/30 bg-amber-500/5 backdrop-blur-xl p-6 sm:p-8 animate-fade-up space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-300" />
              <h2 data-testid="val-status" className="text-lg font-bold text-amber-200">{result.message}</h2>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{result.detail}</p>
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Veri bağlandığında fiyatınız şu kriterlere göre hesaplanacak:</p>
              <div className="flex flex-wrap gap-2">
                {(result.criteria || []).map((c) => (
                  <span key={c} className="text-xs px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-200">{c}</span>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-500 flex items-start gap-1.5 border-t border-amber-500/20 pt-4">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" /> {VALUATION_NOTE}
            </p>
          </div>
        )}

        {result && result.status === "hata" && (
          <div data-testid="val-result" className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 animate-fade-up">
            <p className="flex items-center gap-2 text-red-300 font-semibold"><AlertTriangle className="w-5 h-5" /> {result.message}</p>
            <p className="text-sm text-slate-400 mt-2">{result.detail}</p>
          </div>
        )}

        <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5 flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-400 leading-relaxed">
            Değerleme; aynı marka/model/yıl/paket/motor/yakıt/vites özelliklerine en yakın ilanlar önceliklendirilerek yapılır.
            Kilometre, hasar/tramer kaydı, boya/değişen parça ve bölgesel fiyat farkları hesaba katılır; aykırı (aşırı yüksek/düşük) ilanlar hesaplamadan çıkarılır.
          </p>
        </div>
      </div>
    </div>
  );
}
